<template>
  <div class="ofs-transactions-historic">
    <UIPanel
      v-model="clearedSearch"
      v-model:storage="searchStorage"
      unshrinkable
      placeholder="Search transactions..."
    />
    <UIGridSkeleton
      v-if="disabled"
      :columns
      :sm
      message="Loading transactions..."
    />
    <UIGrid
      v-else
      v-model:sort="sort"
      :columns
      :items
      :sm
      scrollable
    />
    <UIPagination
      :disabled
      :page-no
      :page-size="pageSize"
      :total="pagination?.total"
      @click:page="handleClickPage"
      @update:page-size="handleUpdatePageSize"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onWatcherCleanup, ref, watch } from 'vue'
import { isEqual } from 'lodash'
import { debouncedWatch, watchPausable } from '@vueuse/core'

import {
  Transaction,
  PaginatedMeta,
  Sort,
  FilterComparison,
  FilterLogic,
} from '@types'

import {
  ASSET_TRANSACTIONS_PAGE_SIZE,
  DEBOUNCE_DELAY,
} from '@/const/common'
import {
  TRANSACTION_ENTRY_FIELD,
  TRANSACTION_ENTRY_FIELD_LABEL,
  TRANSACTION_FIELD,
  TRANSACTION_FIELD_LABEL,
} from '@/const'

import { handleCatchedError } from '@/helpers/common'
import { amountTooltip, displayAmount } from '@/entities/transactions/utils/helpers'
import { TransactionAmountRule } from '@/entities/transactions/utils/enums'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useTransactionsStore } from '@/store/transactions'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'

import { UIPagination, UIPanel } from '@ui/core'
import { UIGrid, UIGridSkeleton } from '@ui/grid'

defineOptions({ inheritAttrs: false })

const assetsBunchStore = useAssetsBunchStore()
const transactionsStore = useTransactionsStore()
const transactionsSettingsStore = useTransactionsSettingsStore()

const items = ref<Transaction[]>([])
const pagination = ref<PaginatedMeta>()

const searchStorage = ref<string>()
const clearedSearch = ref<string>()
const sort = ref<Sort[]>()
const pageNo = ref(0)
const pageSize = ref(ASSET_TRANSACTIONS_PAGE_SIZE)
const isLoading = ref(false)

const disabled = computed(
  () => isLoading.value || !transactionsSettingsStore.initFlag,
)

const settingsList = computed(() => transactionsSettingsStore.getList)

const sm = '1fr'
const columns = computed(() => [
  {
    name: TRANSACTION_FIELD.DATE,
    caption: TRANSACTION_FIELD_LABEL[TRANSACTION_FIELD.DATE],
    filter: {
      isDate: true,
    },
  },
  {
    name: TRANSACTION_FIELD.TYPE,
    caption: TRANSACTION_FIELD_LABEL[TRANSACTION_FIELD.TYPE],
  },
  {
    name: 'entries.0.account_id',
    caption: TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.ACCOUNT_ID],
    formatter: (id: string) => assetsBunchStore.getNameById(id),
  },
  {
    name: 'entries.0.amount',
    caption: TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.AMOUNT],
    cellValueClasses: 'ui-grid__cell--right number-node',
    formatter: amount1Formatter,
    tooltip: amountTooltipHandler,
  },
  {
    name: 'entries.0.asset_id',
    caption: TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.ASSET_ID],
    formatter: (id: string) => assetsBunchStore.getNameById(id),
  },
  {
    name: TRANSACTION_FIELD.DESCRIPTION,
    caption: TRANSACTION_FIELD_LABEL[TRANSACTION_FIELD.DESCRIPTION],
  },
])

const fetchOptions = computed(() => ({
  search: clearedSearch.value,
  sort: sort.value?.map((item, index) => ({ ...item, index })),
  filter: {
    params: [
      {
        field: 'type',
        comparison: FilterComparison.EQ,
        value: 'add account',
      },
    ],
    logic: FilterLogic.AND,
  },
}))

const amountFormatter = (value: number | null | undefined, data: Transaction, index: number) => {
  const settings = settingsList.value.find(item => item.name === data.type)?.entries[index]?.amount
  if (!settings) return ''
  const isPercent = settings?.rule === TransactionAmountRule.PERCENT
  return displayAmount(value, isPercent)
}

const amount1Formatter = (value: number | null | undefined, data: Transaction) => {
  return amountFormatter(value, data, 0)
}

const amountTooltipHandler = (field: string, data: Transaction) => {
  if (!data) return
  const fieldIndex = Number(field.replace(/^entries\.(\d+?)\.amount/, '$1'))
  const settings = settingsList.value.find(item => item.name === data.type)
  const value = data.entries[fieldIndex]?.amount
  if (value === null || value === undefined) return
  const isPercent = settings?.entries[fieldIndex]?.amount?.rule === TransactionAmountRule.PERCENT
  return amountTooltip(value, isPercent)
}

const fetch = async (signal?: AbortSignal) => {
  try {
    isLoading.value = true
    const response = await transactionsStore.fetch(
      {
        ...fetchOptions.value,
        page_no: pageNo.value,
        page_size: pageSize.value,
      },
      false,
      signal,
      false,
    )
    items.value = response.data
    pagination.value = response.meta
  } catch (e) {
    handleCatchedError(e as string, fetchOptions.value)
  } finally {
    isLoading.value = false
  }
}

const handleClickPage = (page: number) => {
  pageNo.value = page
}

const handleUpdatePageSize = (newPageSize: number) => {
  if (newPageSize === pageSize.value) return

  pausePageNo()
  pageNo.value = 0
  nextTick(resumePageNo)
  pageSize.value = newPageSize
}

const { pause: pausePageNo, resume: resumePageNo } = watchPausable(
  pageNo,
  () => {
    const controller = new AbortController()
    fetch(controller.signal)
    onWatcherCleanup(() => {
      controller.abort()
    })
  },
  { immediate: true },
)

watch(pageSize, () => {
  const controller = new AbortController()
  fetch(controller.signal)
  onWatcherCleanup(() => {
    controller.abort()
  })
})

debouncedWatch(
  fetchOptions,
  (value, prev) => {
    if (isEqual(value, prev)) return
    const controller = new AbortController()
    fetch(controller.signal)
    onWatcherCleanup(() => {
      controller.abort()
    })
  },
  {
    deep: true,
    debounce: DEBOUNCE_DELAY,
  },
)
</script>

<style>
.ofs-transactions-historic {
  @apply absolute inset-0;
  @apply flex flex-col;
  @apply overflow-hidden;

  .ui-panel,
  .ui-pagination {
    @apply px-2 sm:px-4;
  }

  .ui-panel {
    @apply py-3;
  }

  .ui-grid {
    @apply bg-body;
  }
}
</style>
